
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, Input, EventEmitter, OnChanges, Output, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AppRoutes } from "./app.routes";
import { Observable, BehaviorSubject, Subject } from "rxjs";
import { CommonUtil } from "../../../common/utils/common.utils";
import { TasksRootState } from "../../../task/store";
import { Store } from "@ngrx/store";
import { getFederatedApps } from "../../../reducers";
import { InstallAppDialogComponent } from "../install-app/install-app.component";

import { environment } from "../../../../environments/environment";
import { takeUntil } from "rxjs/operators";
import { ConfigService } from "src/app/common/providers/config.service";
import { ElectronService } from "src/app/task/services/electron.service";
import { StorageService } from "../../../common/services/storage.service";
import { MatDialog } from "@angular/material/dialog";

@Component({
    selector: "vp-app-switcher",
    templateUrl: "app-switcher.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppSwitcherComponent implements OnChanges, OnDestroy {
  matDialog = inject(MatDialog);
  private router = inject(Router);
  private store = inject<Store<TasksRootState>>(Store);
  private electronService = inject(ElectronService);
  private configService = inject(ConfigService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() switchApp: boolean;
  @Input() url: string;
  @Output() closeSwitch = new EventEmitter();
  subscriber: any = null;
  routes: AppRoutes[] = [];
  private isAlive$ = new Subject<boolean>();
  constructor() {

      document.addEventListener("pause", () => {
        window.appInBackground = true;
      });

      this.store.select(getFederatedApps).pipe(takeUntil(this.isAlive$)).subscribe(apps => {
        let routes = [];
        console.log("[AppSwitcherComponent]", apps);
        if ( apps && apps.length === 0) {
          let fedApps = StorageService.getLocalItem("federatedApps");
          if (fedApps) {
            apps = JSON.parse(fedApps);
          }
        }
        apps.forEach(app => {
          routes.push({
            title: app?.options?.title,
            icon: this.configService.get("publicVncDirectoryUrl") + app?.options?.icon,
            image: this.configService.get("publicVncDirectoryUrl") + app?.options?.image,
            nativeLink: `${app?.name?.toLowerCase()}://main`,
            path: app?.options?.url,
            position: app?.options?.position,
            name: app?.name,
            active: true,
            enabled: true
          });
        });
        this.routes = routes;
        this.changeDetectorRef.markForCheck();
      });

  }

  navigate(path: any) {
    this.router.navigate(path);
    this.closeSwitch.emit(false);
  }

  checkInstalledApp(uri: string, appName: string): Observable<boolean> {
    const subject = new BehaviorSubject<boolean>(false);
    if (environment.isCordova) {
      if (CommonUtil.isOnAndroid()) {
        uri = CommonUtil.getPackageName(appName);
      }
      uri = uri.replace("main", "");
      console.log("[checkInstalledApp]", uri);
      appAvailability.check(
        uri, // URI Scheme
        () => {
          console.log("[checkInstalledApp] installed", appName);
          subject.next(true);
        },
        () => {
          console.log("[checkInstalledApp] not installed", appName);
          subject.next(false);
          this.matDialog.open(InstallAppDialogComponent, {
            width: "80%",
            minWidth: "80%",
            maxWidth: "80%",
            autoFocus: false,
            data: { appName: appName }
          });
        }
      );
    } else {
      subject.next(false);
    }

    return subject.asObservable();
  }

  openApp(item): void {
    // The app list comes from the federated app config, only open known VNC hosts.
    if (!CommonUtil.isAllowedAppUrl(item.path)) {
      console.warn("[AppSwitcherComponent][openApp] blocked untrusted app url", item.path);
      this.closeSwitch.emit(false);
      return;
    }
    const path: string = new URL(item.path).origin;
    if (["VNCproject", "VNCdirectory"].indexOf(item.name) > -1) {
      item.path = `${path}/login`;
    }
    if (["VNCtalk", "VNCmail", "VNCcalendar", "VNCtask"].indexOf(item.title) > -1) {
      item.path = `${path}/api/login`;
    }

    if (item.name === "vncchannels") {
      item.nativeLink = "vnctalk://talk/channels";
    }

    if (item.name === "vnccalendar") {
      item.nativeLink = "vncmail://main/calendar";
    }

    if (item.name === "vnctask") {
      // no need to re-open current app!
      this.closeSwitch.emit(false);
      return;
    }


    const nativeLink = CommonUtil.isAllowedNativeLink(item.nativeLink) ? item.nativeLink : null;

    if (!CommonUtil.isOnMobileDevice()) {
      console.log("APPSWITCH openApp: ", item);

      if (this.electronService.isElectron) {
        try {
          let nativeHandler = nativeLink ? this.electronService.app.getApplicationNameForProtocol(nativeLink) : "";
          console.log("appswitcher nativeHandler: ", nativeHandler);
          if (nativeHandler && nativeHandler !== "") {
            this.electronService.openExternalUrl(nativeLink);
          } else {
            this.electronService.openExternalUrl(item.path);
          }
          this.closeSwitch.emit(false);
        } catch (e) {
          console.error("[AppSwitcherComponent] error: ", e);
        }
      } else {
        window.open(item.path, "_blank");
      }
    } else {

      if (nativeLink) {
        window.open(nativeLink, "_system");
      }
      setTimeout(() => {
        console.log("appswitcher checkInBackground: ", window.appInBackground);
        if (!window.appInBackground) {
          window.open(item.path, "_system");
        }
      }, 250);
    }
    this.closeSwitch.emit(false);
  }

  ngOnChanges(): void {
    (this.routes || []).forEach(route => {
      if (route.path === this.url && route.enabled) {
        route.active = true;
      } else {
        route.active = false;
      }
    });
  }

  ngOnDestroy(): void {
    (this.closeSwitch as any).unsubscribe();
    this.isAlive$.next(false);
    this.isAlive$.complete();
  }

}
